import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ExchangeService } from '../../../../Services/exchange.service';
import { Helper } from 'src/app/helpers/helper.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/app/shared/Services/authentication.service';

function emailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }

    if (!String(value).includes('@') || !/@[^@]+\.[^.\s@]+/.test(String(value))) {
      return { invalidEmail: true };
    }

    return null;
  };
}

type ExchangeTargetOption = { id: number; name: string };

type OrderItemOptionForm = FormGroup<{
  orderItemId: FormControl<number>;
  productId: FormControl<number | null>;
  productName: FormControl<string | null>;
  isExchangeable: FormControl<boolean>;
  currentOptionId: FormControl<number>;
  currentOptionName: FormControl<string>;
  selectedTargetId: FormControl<number | null>;
  exchangeOptionId: FormControl<number | null>;
  isLocked: FormControl<boolean>;
  canSelect: FormControl<boolean>;
}>;

// Normalized inputs for building a single option row, shared by the add and edit paths.
interface OptionRowSeed {
  orderItemId: number;
  productId: number | null;
  productName: string | null;
  isExchangeable: boolean;
  currentOptionId: number;
  currentOptionName: string;
  exchangeOptionId: number | null;
  defaultTargetId: number | null;
  canSelect: boolean;
}

type ExchangeItemForm = FormGroup<{
  orderItemId: FormControl<number>;
  label: FormControl<string>;
  allowSeparateSelection: FormControl<boolean>;
  orderItemOptions: FormArray<OrderItemOptionForm>;
}>;

type CustomerForm = FormGroup<{
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  street: FormControl<string>;
  houseNumber: FormControl<string>;
  postalCode: FormControl<string>;
  city: FormControl<string>;
  email: FormControl<string>;
  telephoneNumber: FormControl<string>;
  land: FormControl<string>;
  consentA: FormControl<boolean>;
  consentB: FormControl<boolean>;
}>;

type ExchangeForm = FormGroup<{
  orderId: FormControl<number>;
  items: FormArray<ExchangeItemForm>;
  customer: CustomerForm;
  status: FormControl<string>;
  dealerNote: FormControl<string>;
  logisticNote: FormControl<string>;
}>;

type DateValue = string | Date | null;

type ExchangeInfo = {
  createdAt: DateValue;
  serviceNumber: string | null;
  exhangeStatus: string | null;
  dealerNote: string | null;
  logisticNote: string | null;
  physicalDeliveryAt: DateValue;
};

@Component({
  selector: 'app-manage-exchange',
  templateUrl: './manage-exchange.component.html',
  styleUrls: ['./manage-exchange.component.scss'],
})
export class ManageExchangeComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  days: number | null = null;
  orderNo: string | null = null;
  // Exchange target choices are the same for every row of a given product, so cache them by product id.
  private readonly optionTargetsByProductId = new Map<number, ExchangeTargetOption[]>();
  isEditMode = false;
  isEditing = false;
  private exchangeId: number | null = null;
  isExchangeLocked = false;

  exchangeInfo: ExchangeInfo = {
    createdAt: null,
    serviceNumber: null,
    exhangeStatus: null,
    dealerNote: null,
    logisticNote: null,
    physicalDeliveryAt: null,
  };

  form: ExchangeForm = this.fb.group({
    orderId: this.fb.nonNullable.control(0),
    items: this.fb.nonNullable.array<ExchangeItemForm>([]),
    customer: this.fb.nonNullable.group({
      firstName: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      lastName: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      street: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      houseNumber: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      postalCode: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      city: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      email: this.fb.nonNullable.control('', {
        validators: [Validators.required, emailValidator()],
      }),
      telephoneNumber: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      land: this.fb.nonNullable.control('Netherlands', { validators: [Validators.required] }),
      consentA: this.fb.nonNullable.control(false),
      consentB: this.fb.nonNullable.control(false),
    }),
    status: this.fb.nonNullable.control('open'),
    dealerNote: this.fb.nonNullable.control(''),
    logisticNote: this.fb.nonNullable.control(''),
  });

  // TODO: replace with values fetched from the API once the endpoint exists (see service-request statuses pattern).
  readonly statusOptions: ReadonlyArray<{ id: string; translationKey: string }> = [
    { id: 'open', translationKey: 'exchange.statuses.open' },
    { id: 'closed', translationKey: 'exchange.statuses.closed' },
    { id: 'canceled', translationKey: 'exchange.statuses.canceled' },
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly exchangeService: ExchangeService,
    private readonly helper: Helper,
    private readonly translator: TranslateService,
    private readonly router: Router,
    private auth: AuthenticationService,
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const orderId = idParam ? Number(idParam) : 0;

    this.form.controls.orderId.setValue(orderId);

    const routePath = this.route.snapshot.routeConfig?.path ?? '';
    const isEdit = routePath.includes('edit') || this.route.snapshot.url.some((s) => s.path === 'edit');
    this.isEditMode = isEdit;

    // Add route is editable by default; edit route is view-first (read-only until "Edit" is clicked).
    this.isEditing = !isEdit;

    // Apply the initial editable/read-only state (also keeps the edit route read-only while loading).
    this.applyEditableState();

    const request$ = isEdit ? this.exchangeService.getSingleExchange(orderId) : this.exchangeService.getExchangeOrder(orderId);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe((order) => this.buildFormFromOrder(order));

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get itemsArray(): FormArray<ExchangeItemForm> {
    return this.form.controls.items;
  }

  get emailControl(): FormControl<string> {
    return this.form.controls.customer.controls.email;
  }

  get emailErrorKey(): string | null {
    return this.emailControl.errors?.['invalidEmail'] ? 'exchange.errors.email' : null;
  }

  get hasVisibleExchangeSelects(): boolean {
    return this.itemsArray.controls.some((item) => this.itemHasVisibleExchangeOptions(item));
  }

  itemHasVisibleExchangeOptions(itemForm: ExchangeItemForm): boolean {
    return itemForm.controls.orderItemOptions.controls.some(
      (opt) => opt.controls.isExchangeable.value && this.hasExchangeTargetChoices(opt),
    );
  }

  private noOptionTarget(): ExchangeTargetOption {
    return { id: -1, name: this.translator.instant('exchange.no_option') };
  }

  private targetsForProduct(optionForm: OrderItemOptionForm): ExchangeTargetOption[] | undefined {
    const productId = optionForm.controls.productId.value;
    return productId == null ? undefined : this.optionTargetsByProductId.get(productId);
  }

  targetsForOption(optionForm: OrderItemOptionForm): ExchangeTargetOption[] {
    return this.targetsForProduct(optionForm) ?? [this.noOptionTarget()];
  }

  hasExchangeTargetChoices(optionForm: OrderItemOptionForm): boolean {
    return !!this.targetsForProduct(optionForm)?.some((t) => t.id !== -1);
  }

  hasMultipleVisibleExchangeOptions(itemForm: ExchangeItemForm): boolean {
    return itemForm.controls.orderItemOptions.controls.filter(
      (opt) => opt.controls.isExchangeable.value && this.hasExchangeTargetChoices(opt),
    ).length > 0;
  }

  isOptionSelectionDisabled(optionForm: OrderItemOptionForm): boolean {
    return !optionForm.controls.canSelect.value;
  }

  // Track form-control identity so *ngFor reuses rows instead of re-rendering on every change-detection pass.
  trackByControl(_index: number, ctrl: AbstractControl): AbstractControl {
    return ctrl;
  }

  buildPayload(): unknown {
    const base = this.form.getRawValue();

    // A status change applies to the whole exchange: every orderExchangeOption gets the new status.
    const status = base.status;

    const orderExchangeOptions = base.items.reduce(
      (acc, item) => {
        for (const opt of item.orderItemOptions) {
          if (opt.isLocked) continue;
          if (typeof opt.productId !== 'number') continue;

          const entry: {
            id?: number;
            orderOption: number;
            exchangeWith: number | null;
            product: number;
            orderItem: number;
            amount: 1;
            status: string;
          } = {
            orderOption: opt.currentOptionId,
            exchangeWith: opt.selectedTargetId === null || opt.selectedTargetId === -1 ? null : opt.selectedTargetId,
            product: opt.productId,
            orderItem: opt.orderItemId,
            amount: 1,
            status,
          };

          // Edit mode: carry the existing record id so the backend updates in place instead of inserting.
          if (this.isEditMode && typeof opt.exchangeOptionId === 'number') {
            entry.id = opt.exchangeOptionId;
          }

          acc.push(entry);
        }
        return acc;
      },
      [] as Array<{
        id?: number;
        orderOption: number;
        exchangeWith: number | null;
        product: number;
        orderItem: number;
        amount: 1;
        status: string;
      }>
    );

    const {
      consentA: _consentA,
      consentB: _consentB,
      firstName,
      lastName,
      telephoneNumber,
      email,
      street,
      houseNumber,
      postalCode,
      city,
      land,
    } = base.customer;

    return {
      orderExchangeOptions,
      customerName: `${firstName} ${lastName}`.trim(),
      customerPhone: telephoneNumber,
      customerEmail: email,
      customerStreet: street,
      customerHouseNumber: houseNumber,
      customerPostalCode: postalCode,
      customerCity: city,
      customerLand: land,
      dealerNote: base.dealerNote ?? '',
      logisticNote: base.logisticNote ?? '',
    };
  }

  submit(): void {
    if (this.isExchangeLocked) return;
    if (!this.hasSelectedExchange) {
      this.helper.popMessageDialog(
        this.translator.instant('global.warning'),
        'exchange.warning',
        'warning',
        false
      );
      return;
    }
    const payload = this.buildPayload();
    const orderId = this.form.controls.orderId.value;

    const req$ =
      this.isEditMode && typeof this.exchangeId === 'number'
        ? this.exchangeService.updateExchange(this.exchangeId, payload)
        : this.exchangeService.createExchange(orderId, payload);

    req$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.helper.popMessageDialog(
          this.translator.instant('global.success_message.title'),
          this.translator.instant('exchange.add.success_massage'),
          'success',
          false
        );
        if(this.auth.isBackOffice()) this.router.navigateByUrl('/back-office/exchange');
        else this.router.navigateByUrl('/dealer-system/exchange');
    });
  }

  cancel(): void {
    if (!this.isEditMode) return;
    if (typeof this.exchangeId !== 'number') return;
    if (this.isExchangeLocked) return;

    this.helper
      .popConfirmDialog(
        this.translator.instant('global.warning'),
        'Are you sure you want to cancel this exchange?',
        'warning',
        false,
        this.translator.instant('global.yes'),
        this.translator.instant('global.cancel')
      )
      .then((res) => {
        if (!res || !res.value) return;

        this.exchangeService
          .deleteSingleExchange(this.exchangeId as number)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            if (this.auth.isBackOffice()) this.router.navigateByUrl('/back-office/exchange');
            else this.router.navigateByUrl('/dealer-system/exchange');
          });
      });
  }

  get canSubmit(): boolean {
    const customer = this.form.controls.customer.getRawValue();
    return customer.consentA && customer.consentB;
  }

  get canEditDealerNote(): boolean {
    return this.auth.isDealer() || this.auth.isBackOffice();
  }

  get canEditLogisticNote(): boolean {
    return this.auth.isBackOffice();
  }

  get canEditStatus(): boolean {
    return this.auth.isBackOffice();
  }

  enableEditing(): void {
    if (this.isExchangeLocked) return;
    this.isEditing = true;
    this.applyEditableState();
  }

  private applyEditableState(): void {
    if (!this.isEditing || this.isExchangeLocked) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });

    // Re-apply the per-control restrictions that a blanket enable() would otherwise clear.
    for (const itemCtrl of this.form.controls.items.controls) {
      for (const optCtrl of itemCtrl.controls.orderItemOptions.controls) {
        if (!optCtrl.controls.canSelect.value) {
          optCtrl.controls.selectedTargetId.disable({ emitEvent: false });
        }
      }
    }

    if (!this.canEditDealerNote) {
      this.form.controls.dealerNote.disable({ emitEvent: false });
    }
    if (!this.canEditLogisticNote) {
      this.form.controls.logisticNote.disable({ emitEvent: false });
    }
    if (!this.canEditStatus) {
      this.form.controls.status.disable({ emitEvent: false });
    }
  }

  get hasSelectedExchange(): boolean {
    for (const item of this.form.controls.items.controls) {
      for (const opt of item.controls.orderItemOptions.controls) {
        if (opt.controls.isLocked.value) continue;
        if (!opt.controls.canSelect.value) continue;
        const targetId = opt.controls.selectedTargetId.value;
        const productId = opt.controls.productId.value;
        if (targetId === null) continue;
        if (targetId === -1) continue;
        // A new option equal to the current option is not an exchange.
        if (targetId === opt.controls.currentOptionId.value) continue;
        if (typeof productId !== 'number') continue;
        return true;
      }
    }
    return false;
  }

  private buildFormFromOrder(order: any): void {
    this.exchangeInfo = this.extractExchangeInfo(order);
    this.form.controls.dealerNote.setValue(this.exchangeInfo.dealerNote ?? '', { emitEvent: false });
    this.form.controls.logisticNote.setValue(this.exchangeInfo.logisticNote ?? '', { emitEvent: false });

    this.applyLoadedStatus(order);

    this.days = order?.days ?? null;
    if (this.isEditMode) {
      const id = Number(order?.id);
      this.exchangeId = Number.isFinite(id) ? id : null;
    }

    this.optionTargetsByProductId.clear();

    // Edit: build straight from the saved exchange options. Add: build from the order's items.
    let rawOrderItems: any[];
    if (this.isEditMode) {
      rawOrderItems = Array.isArray(order?.orderExchangeOptions) ? order.orderExchangeOptions.map((x: any) => x?.orderItem).filter(Boolean) : [];
    } else {
      rawOrderItems = Array.isArray(order?.orderItems) ? order.orderItems : [];
    }
    this.orderNo = this.resolveOrderNo(order, rawOrderItems);

    this.form.setControl('items', this.isEditMode ? this.buildItemsForEdit(order) : this.buildItemsForAdd(order));

    this.patchCustomer(order);

    // Default flow: everything is read-only (view mode) until the user clicks "Edit".
    this.applyEditableState();
  }

  private readStatusRaw(order: any): string | null {
    return (
      order?.exhangeStatus ??
      order?.exchangeStatus ??
      order?.exchange_status ??
      order?.status ??
      order?.exchange?.exhangeStatus ??
      order?.exchange?.exchangeStatus ??
      null
    );
  }

  private extractExchangeInfo(order: any): ExchangeInfo {
    return {
      createdAt:
        order?.createdAt ??
        order?.exchange?.createdAt ??
        order?.order?.createdAt ??
        order?.order?.exchange?.createdAt ??
        null,
      serviceNumber:
        order?.serviceNumber ??
        order?.service_no ??
        order?.serviceNumberText ??
        order?.exchange?.serviceNumber ??
        order?.exchange?.service_no ??
        null,
      exhangeStatus: (this.readStatusRaw(order) as string | null) ?? null,
      dealerNote: order?.dealerNote ?? order?.dealer_note ?? order?.exchange?.dealerNote ?? null,
      logisticNote: order?.logisticNote ?? order?.logistic_note ?? order?.exchange?.logisticNote ?? null,
      physicalDeliveryAt: order.physicalDeliveryAt ?? null,
    };
  }

  private mapStatusToOption(normalizedStatus: string): string {
    const exact = this.statusOptions.find((s) => s.id === normalizedStatus)?.id;
    if (exact) return exact;
    if (normalizedStatus.includes('cancel')) return 'canceled';
    if (normalizedStatus.includes('close')) return 'closed';
    return 'open';
  }

  private applyLoadedStatus(order: any): void {
    const normalizedStatus = String(this.readStatusRaw(order) ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');

    // Map the loaded status onto one of the known options so the dropdown preselects correctly.
    this.form.controls.status.setValue(this.mapStatusToOption(normalizedStatus), { emitEvent: false });

    // A canceled OR closed exchange can no longer be edited.
    this.isExchangeLocked = normalizedStatus.includes('cancel') || normalizedStatus.includes('close');
  }

  private resolveOrderNo(order: any, orderItems: any[]): string | null {
    const fromSingleExchange = this.isEditMode
      ? (order?.orderNumber ?? order?.order_number ?? order?.order?.orderNumber ?? order?.order?.order_number ?? null)
      : null;

    return (
      fromSingleExchange ??
      order?.orderNo ??
      order?.order_no ??
      order?.order?.orderNo ??
      order?.order?.order_no ??
      orderItems?.[0]?.orderNo ??
      orderItems?.[0]?.order_no ??
      null
    );
  }

  // ----- Edit mode: build straight from the saved exchange options, grouped by order item -----

  private buildItemsForEdit(order: any): FormArray<ExchangeItemForm> {
    const exchangeOptions: any[] = Array.isArray(order?.orderExchangeOptions) ? order.orderExchangeOptions : [];

    const groups = new Map<number, any[]>();
    for (const ex of exchangeOptions) {
      const itemId = Number(ex?.orderItem?.id);
      if (!Number.isFinite(itemId)) continue;
      const existing = groups.get(itemId);
      if (existing) existing.push(ex);
      else groups.set(itemId, [ex]);
    }

    const itemsFA = this.fb.nonNullable.array<ExchangeItemForm>([]);
    for (const [itemId, entries] of groups) {
      const optionsFA = this.fb.nonNullable.array<OrderItemOptionForm>([]);
      for (const ex of entries) {
        optionsFA.push(this.createOptionRow(this.editSeed(itemId, ex)));
      }
      itemsFA.push(this.buildItemGroup(itemId, entries[0]?.orderItem, optionsFA));
    }
    return itemsFA;
  }

  private editSeed(itemId: number, ex: any): OptionRowSeed {
    const orderOption = ex?.orderOption ?? {};
    const isExchangeable = !!orderOption?.isExchangeable;
    const exchangeWithId = Number(ex?.exchangeWith?.id ?? ex?.exchangeWith);
    const exchangeOptionId = Number(ex?.id);

    return {
      orderItemId: itemId,
      productId: ex?.product?.id ?? null,
      productName: ex?.product?.name ?? null,
      isExchangeable,
      currentOptionId: Number(orderOption?.id),
      currentOptionName: this.optionName(orderOption),
      exchangeOptionId: Number.isFinite(exchangeOptionId) ? exchangeOptionId : null,
      // The saved exchangeWith becomes the dropdown's default selected value.
      defaultTargetId: Number.isFinite(exchangeWithId) ? exchangeWithId : -1,
      canSelect: isExchangeable && !this.isExchangeLocked,
    };
  }

  // ----- Add mode: build from the order's items / options (urbanOption-based) -----

  private buildItemsForAdd(order: any): FormArray<ExchangeItemForm> {
    const orderItems: any[] = Array.isArray(order?.orderItems) ? order.orderItems : [];
    const rootOrderItemOptions: any[] = Array.isArray(order?.orderItemOptions) ? order.orderItemOptions : [];

    const itemsFA = this.fb.nonNullable.array<ExchangeItemForm>([]);
    for (const item of orderItems) {
      const itemOptions: any[] =
        Array.isArray(item?.orderItemOptions) && item.orderItemOptions.length
          ? item.orderItemOptions
          : rootOrderItemOptions.filter((x) => x?.orderItemId === item?.id);

      const optionsFA = this.fb.nonNullable.array<OrderItemOptionForm>([]);
      for (const opt of itemOptions) {
        optionsFA.push(this.createOptionRow(this.addSeed(item, opt)));
      }
      itemsFA.push(this.buildItemGroup(item?.id, item, optionsFA));
    }
    return itemsFA;
  }

  private addSeed(item: any, opt: any): OptionRowSeed {
    const isExchangeable = !!opt?.urbanOption?.isExchangeable;
    const hasAnyOpenExchange = opt?.urbanOption?.hasAnyOpenExchang ?? opt?.urbanOption?.hasAnyOpenExchange ?? true;

    return {
      orderItemId: item?.id,
      productId: opt?.product?.id ?? null,
      productName: opt?.product?.name ?? null,
      isExchangeable,
      currentOptionId: Number(opt?.urbanOption?.id),
      currentOptionName: this.optionName(opt?.urbanOption),
      exchangeOptionId: null,
      defaultTargetId: -1,
      canSelect: isExchangeable && !hasAnyOpenExchange && !this.isExchangeLocked,
    };
  }

  // ----- Shared builders -----

  private buildItemGroup(itemId: number, orderItem: any, optionsFA: FormArray<OrderItemOptionForm>): ExchangeItemForm {
    const itemGroup = this.fb.nonNullable.group({
      orderItemId: this.fb.nonNullable.control(itemId),
      label: this.fb.nonNullable.control(this.resolveItemLabel(orderItem, itemId)),
      allowSeparateSelection: this.fb.nonNullable.control(false),
      orderItemOptions: optionsFA,
    });

    this.wireItemSelectionSync(itemGroup);

    return itemGroup;
  }

  private resolveItemLabel(orderItem: any, itemId: number): string {
    return orderItem?.collection?.name ?? orderItem?.collectionName ?? orderItem?.collection_name ?? `Order item #${itemId}`;
  }

  private optionName(option: any): string {
    if (!option) return '';
    if (option.name) return String(option.name);
    const translation = Array.isArray(option.translations) ? option.translations[0] : undefined;
    return translation?.content ? String(translation.content) : '';
  }

  private createOptionRow(seed: OptionRowSeed): OrderItemOptionForm {
    const canSelect = seed.canSelect;

    const rowCtrl = this.fb.nonNullable.group({
      orderItemId: this.fb.nonNullable.control(seed.orderItemId),
      productId: this.fb.control<number | null>(seed.productId),
      productName: this.fb.control<string | null>(seed.productName),
      isExchangeable: this.fb.nonNullable.control(seed.isExchangeable),
      currentOptionId: this.fb.nonNullable.control(seed.currentOptionId),
      currentOptionName: this.fb.nonNullable.control(seed.currentOptionName),
      selectedTargetId: this.fb.control<number | null>(canSelect ? (seed.defaultTargetId ?? -1) : null),
      exchangeOptionId: this.fb.control<number | null>(seed.exchangeOptionId),
      isLocked: this.fb.nonNullable.control(!canSelect),
      canSelect: this.fb.nonNullable.control(canSelect),
    });

    if (!canSelect) {
      rowCtrl.controls.selectedTargetId.disable({ emitEvent: false });
    }

    if (typeof seed.productId === 'number') {
      this.ensureTargetsLoaded(seed.productId);
    }

    return rowCtrl;
  }

  private ensureTargetsLoaded(productId: number): void {
    if (this.optionTargetsByProductId.has(productId)) return;

    // Seed with the "no option" entry so repeat callers short-circuit and the dropdown has a base choice.
    this.optionTargetsByProductId.set(productId, [this.noOptionTarget()]);

    this.exchangeService
      .getExchangeOptionsForProduct(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => {
        this.optionTargetsByProductId.set(productId, [this.noOptionTarget(), ...list]);
      });
  }

  // A target option id is only valid for a row whose product actually offers it
  // (each product has its own choices from getExchangeOptionsForProduct). The "no option" (-1) is always valid.
  private rowSupportsTarget(row: OrderItemOptionForm, targetId: number): boolean {
    if (targetId === -1) return true;
    const productId = row.controls.productId.value;
    if (productId == null) return false;
    return !!this.optionTargetsByProductId.get(productId)?.some((t) => t.id === targetId);
  }

  private wireItemSelectionSync(itemGroup: ExchangeItemForm): void {
    itemGroup.controls.allowSeparateSelection.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((allowSeparate) => {
        if (allowSeparate) return;

        const rows = itemGroup.controls.orderItemOptions.controls;
        const reference = rows.map((r) => r.controls.selectedTargetId.value).find((v) => v !== null) ?? null;

        if (reference === null) return;

        for (const row of rows) {
          if (row.controls.isLocked.value) continue;
          // Don't force a target onto a product that doesn't offer it — that would mismatch product ↔ exchangeWith.
          if (!this.rowSupportsTarget(row, reference)) continue;
          row.controls.selectedTargetId.setValue(reference, { emitEvent: false });
        }
      });

    for (const rowCtrl of itemGroup.controls.orderItemOptions.controls) {
      rowCtrl.controls.selectedTargetId.valueChanges
        .pipe(takeUntil(this.destroy$), distinctUntilChanged())
        .subscribe((targetId) => {
          if (targetId === null) return;
          if (itemGroup.controls.allowSeparateSelection.value) return;

          for (const otherRow of itemGroup.controls.orderItemOptions.controls) {
            if (otherRow === rowCtrl) continue;
            if (otherRow.controls.isLocked.value) continue;
            if (this.isOptionSelectionDisabled(otherRow)) continue;
            // Only mirror the choice to siblings whose product actually offers it; otherwise leave them as-is.
            if (!this.rowSupportsTarget(otherRow, targetId)) continue;
            otherRow.controls.selectedTargetId.setValue(targetId, { emitEvent: false });
          }
        });
    }
  }

  private patchCustomer(order: any): void {
    if (!this.isEditMode) {
      this.form.controls.customer.setValue(
        {
          firstName: '',
          lastName: '',
          street: '',
          houseNumber: '',
          postalCode: '',
          city: '',
          email: '',
          telephoneNumber: '',
          land: 'Netherlands',
          consentA: false,
          consentB: false,
        },
        { emitEvent: false }
      );
      return;
    }

    const customerName: string =
      order?.customerName ??
      order?.customer?.name ??
      order?.order?.customerName ??
      `${order?.user?.firstName ?? ''} ${order?.user?.lastName ?? ''}`.trim();

    const nameParts = String(customerName || '').trim().split(/\s+/).filter(Boolean);
    const firstNameFromName = nameParts[0] ?? '';
    const lastNameFromName = nameParts.slice(1).join(' ');

    this.form.controls.customer.patchValue({
      firstName:
        order?.customerFirstName ??
        order?.customer?.firstName ??
        order?.order?.customerFirstName ??
        order?.user?.firstName ??
        firstNameFromName,
      lastName:
        order?.customerLastName ??
        order?.customer?.lastName ??
        order?.order?.customerLastName ??
        order?.user?.lastName ??
        lastNameFromName,
      street:
        order?.customerStreet ??
        order?.customer?.street ??
        order?.order?.customerStreet ??
        order?.user?.deliveryContact?.address ??
        '',
      houseNumber:
        order?.customerHouseNumber ??
        order?.customer?.houseNumber ??
        order?.order?.customerHouseNumber ??
        order?.user?.deliveryContact?.houseNumber ??
        '',
      postalCode:
        order?.customerPostalCode ??
        order?.customer?.postalCode ??
        order?.order?.customerPostalCode ??
        order?.user?.deliveryContact?.postalCode ??
        '',
      city:
        order?.customerCity ??
        order?.customer?.city ??
        order?.order?.customerCity ??
        order?.user?.deliveryContact?.city ??
        '',
      email:
        order?.customerEmail ??
        order?.customer?.email ??
        order?.order?.customerEmail ??
        order?.user?.email ??
        '',
      telephoneNumber:
        order?.customerPhone ??
        order?.customerTelephoneNumber ??
        order?.customer?.telephoneNumber ??
        order?.customer?.phone ??
        order?.order?.customerPhone ??
        order?.user?.deliveryContact?.phone ??
        '',
      land: order?.customerLand ?? order?.customer?.land ?? order?.order?.customerLand ?? 'Netherlands',
    });
  }
}

